import { useEffect, useRef, useState } from "react";
import { api, API } from "../lib/api";
import { refreshBranding, useBranding } from "../lib/branding";
import { toast } from "sonner";
import { Loader2, Save, Settings as SettingsIcon, Upload, Trash2, Image as ImageIcon } from "lucide-react";

export default function Configuration() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileRef = useRef(null);
  const branding = useBranding();

  const loadSettings = () => api.get("/admin/settings").then((r) => setForm(r.data));

  useEffect(() => { loadSettings(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Exclude logo url from payload (handled by upload endpoint)
      const { company_logo_url, ...payload } = form;
      const r = await api.put("/admin/settings", payload);
      setForm(r.data);
      await refreshBranding();
      toast.success("Configuration enregistrée");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const onLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image trop volumineuse (max 2 Mo)");
      return;
    }
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/admin/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refreshBranding();
      await loadSettings();
      toast.success("Logo mis à jour");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur d'upload");
    } finally {
      setUploadingLogo(false);
    }
  };

  const onLogoDelete = async () => {
    if (!window.confirm("Supprimer le logo actuel ?")) return;
    setUploadingLogo(true);
    try {
      await api.delete("/admin/settings/logo");
      await refreshBranding();
      await loadSettings();
      toast.success("Logo supprimé");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setUploadingLogo(false);
    }
  };

  if (!form) return <div className="text-slate-400 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Chargement…</div>;

  const logoSrc = branding.company_logo_url;

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-btp-terra font-semibold mb-2">Administration</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Configuration</h1>
        <p className="text-sm text-slate-500 mt-1">Paramètres globaux de la plateforme — réservé au super administrateur</p>
      </div>

      <form onSubmit={submit} className="bg-white border border-slate-200 rounded-md p-6 space-y-6" data-testid="settings-form">
        <Section title="Identité de l'entreprise" icon={SettingsIcon}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Nom de l'entreprise"><input value={form.company_name || ""} onChange={set("company_name")} className="input" data-testid="settings-company-name" /></Field>
            <Field label="Slogan"><input value={form.company_tagline || ""} onChange={set("company_tagline")} className="input" data-testid="settings-tagline" /></Field>
          </div>

          {/* Logo upload */}
          <div className="mt-5 p-4 rounded-md bg-slate-50 border border-slate-200" data-testid="logo-uploader">
            <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Logo de l'entreprise</label>
            <div className="mt-3 flex items-center gap-4">
              <div className="w-20 h-20 rounded-md bg-white border border-slate-200 flex items-center justify-center overflow-hidden shrink-0">
                {logoSrc ? (
                  <img src={logoSrc} alt="logo" className="w-full h-full object-contain" data-testid="logo-preview" />
                ) : (
                  <ImageIcon size={28} className="text-slate-300" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-slate-500 mb-2">Format conseillé : PNG ou SVG, carré, ≤ 2&nbsp;Mo. Affiché sur la page de connexion, la sidebar et l'en-tête des exports Word.</div>
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={() => fileRef.current?.click()} disabled={uploadingLogo}
                    className="h-9 px-3 bg-btp-navy hover:bg-btp-navy/90 disabled:opacity-60 text-white rounded-md text-xs font-semibold flex items-center gap-1.5"
                    data-testid="logo-upload-btn">
                    {uploadingLogo ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                    {logoSrc ? "Remplacer" : "Téléverser un logo"}
                  </button>
                  {logoSrc && (
                    <button type="button" onClick={onLogoDelete} disabled={uploadingLogo}
                      className="h-9 px-3 border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-60 rounded-md text-xs font-semibold flex items-center gap-1.5"
                      data-testid="logo-delete-btn">
                      <Trash2 size={13} /> Supprimer
                    </button>
                  )}
                </div>
                <input ref={fileRef} type="file" accept="image/*" onChange={onLogoUpload} className="hidden" data-testid="logo-file-input" />
              </div>
            </div>
          </div>
        </Section>

        <Section title="Localisation & projets">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Pays par défaut"><input value={form.default_country || ""} onChange={set("default_country")} className="input" data-testid="settings-country" /></Field>
            <Field label="Devise"><input value={form.default_currency || ""} onChange={set("default_currency")} className="input" data-testid="settings-currency" /></Field>
            <Field label="Préfixe code projet"><input value={form.project_code_prefix || ""} onChange={set("project_code_prefix")} className="input" data-testid="settings-code-prefix" /></Field>
          </div>
        </Section>

        <Section title="Fonctionnalités">
          <div className="space-y-3">
            <Toggle label="Assistant IA activé" checked={form.ai_enabled} onChange={set("ai_enabled")} testid="settings-ai-enabled" />
            <Toggle label="Notifications temps réel" checked={form.notifications_enabled} onChange={set("notifications_enabled")} testid="settings-notif-enabled" />
            <Toggle label="Inscription publique autorisée (sinon : compte créé uniquement par un super admin)" checked={form.public_registration_enabled} onChange={set("public_registration_enabled")} testid="settings-public-reg" />
          </div>
        </Section>

        <div className="flex justify-end pt-2 border-t border-slate-200">
          <button type="submit" disabled={saving} className="h-10 px-4 bg-btp-terra hover:bg-btp-terra-hover disabled:opacity-60 text-white rounded-md text-sm font-semibold flex items-center gap-2" data-testid="settings-save">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Enregistrer
          </button>
        </div>
      </form>

      <style>{`.input{ margin-top:0.375rem; width:100%; height:2.5rem; padding:0 0.75rem; border:1px solid #cbd5e1; border-radius:0.375rem; font-size:0.875rem; } .input:focus{ outline:none; border-color:#1e3a5f; box-shadow:0 0 0 1px #1e3a5f; }`}</style>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-100">
        {Icon && <Icon size={16} className="text-btp-navy" />}
        <h3 className="font-heading font-bold text-sm uppercase tracking-wider text-slate-700">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function Field({ label, className = "", children }) {
  return (
    <div className={className}>
      <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">{label}</label>
      {children}
    </div>
  );
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer">
      <input type="checkbox" checked={!!checked} onChange={onChange} className="w-4 h-4 accent-btp-terra" data-testid={testid} />
      <span className="text-sm text-slate-700">{label}</span>
    </label>
  );
}
